import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { CONTACT, IMAGES } from '../mock/data';
import useScrollReveal from '../hooks/useScrollReveal';
import Seo from '../components/Seo';

const initialForm = { name: '', email: '', message: '' };

export default function Contact() {
  useScrollReveal();
  const [form, setForm] = useState(initialForm);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all fields.');
      return;
    }
    const lines = [
      '*New Message — Vivamore*',
      '',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Message: ${form.message}`
    ].join('\n');
    const url = `https://wa.me/${CONTACT.phoneRaw}?text=${encodeURIComponent(lines)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
    toast.success('Opening WhatsApp — just tap Send to submit your message.');
    setForm(initialForm);
  };

  const inputCls = 'w-full bg-transparent border-b border-ink/20 py-3 text-ink placeholder-ink/40 focus:outline-none focus:border-gold transition-colors font-light';
  const labelCls = 'text-xs tracking-widest uppercase text-ink/60 mb-2 block';

  const INFO = [
    { icon: MapPin, title: 'Location', value: CONTACT.address },
    { icon: Phone, title: 'Phone', value: CONTACT.phone, href: CONTACT.whatsapp },
    { icon: Mail, title: 'Email', value: CONTACT.email, href: `mailto:${CONTACT.email}` },
    { icon: Clock, title: 'Hours', value: CONTACT.hours }
  ];

  return (
    <div>
      <Seo
        title="Contact & Reservations"
        path="/contact"
        description="Get in touch with Vivamore Dining at Level 6, NU Sentral Mall, Kuala Lumpur. Reserve a table or plan your event — call/WhatsApp +60 19-654 7318."
        keywords="Vivamore contact, reservation KL, NU Sentral restaurant contact, rooftop dining reservation, event enquiry Kuala Lumpur"
      />
      <section className="relative h-[52vh] min-h-[380px] flex items-center justify-center overflow-hidden">
        <img src={IMAGES.diningAmbiance} alt="Contact" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/65" />
        <div className="relative z-10 text-center px-6">
          <span className="text-cream/80 text-xs tracking-luxe uppercase">We'd love to hear from you</span>
          <h1 className="font-display text-5xl md:text-6xl text-cream mt-5">Get in Touch</h1>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-cream">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-16">
          <div className="reveal">
            <span className="text-xs tracking-luxe uppercase text-gold">Contact Details</span>
            <h2 className="font-display text-4xl text-ink mt-4">Visit Vivamore</h2>
            <div className="gold-line my-8" />
            <div className="space-y-8">
              {INFO.map((c) => (
                <div key={c.title} className="flex gap-5">
                  <div className="w-12 h-12 border border-gold/40 flex items-center justify-center shrink-0">
                    <c.icon size={20} className="text-gold" />
                  </div>
                  <div>
                    <p className="text-xs tracking-widest uppercase text-ink/50">{c.title}</p>
                    {c.href ? (
                      <a href={c.href} target="_blank" rel="noopener noreferrer" className="text-ink font-light text-lg hover:text-gold transition-colors">{c.value}</a>
                    ) : (
                      <p className="text-ink font-light text-lg">{c.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <a href={CONTACT.whatsappReserve} target="_blank" rel="noopener noreferrer" className="btn-gold inline-flex items-center gap-2 mt-10 text-xs tracking-widest uppercase px-8 py-4 bg-gold text-cream border border-gold hover:bg-transparent hover:text-gold">
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>

          <div className="reveal bg-white shadow-sm p-8 md:p-10">
            <h3 className="font-serif text-2xl text-ink mb-8">Send us a message</h3>
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <label className={labelCls}>Full Name</label>
                <input className={inputCls} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" />
              </div>
              <div>
                <label className={labelCls}>Email Address</label>
                <input type="email" className={inputCls} value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className={labelCls}>Message</label>
                <textarea rows={5} className={inputCls} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="How can we help?" />
              </div>
              <button type="submit" className="btn-gold w-full inline-flex items-center justify-center gap-2 text-xs tracking-widest uppercase px-8 py-4 bg-ink text-cream border border-ink hover:bg-gold hover:border-gold">
                <MessageCircle size={16} /> Send via WhatsApp
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
